import {
  EPropertyType,
  getQuickPropShapeIssue,
  IComponent,
  IComponentDefinition,
  IDisplayItem,
  IPropertyDefinition,
  IQuickProp,
  IQuickPropertyGroupRef,
  QuickPropShapeIssue
} from "@kuika/kuika-cl-model";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { collapsePropsStable, IBarBudget, selectionBarBudget } from "../collapse";
import { ISelectionOverlaySd } from "../selection-actions";
import {
  QuickChip,
  QuickDismissContext,
  QuickOpenScope,
  QuickPopover,
  QuickTip,
  useQuickOpenKey
} from "./quick-controls";
import { toDisplayLabel } from "./quick-labels";
import "./quick-props.scss";
import {
  IResolvedQuickProperty,
  QuickPropertyControl,
  QuickPropertyGroup,
  resolveQuickProperties
} from "./quick-property-controls";
import { IQuickContext, warnSkippedQuickProp } from "./quick-shared";
import {
  QuickChildrenControl,
  QuickCommandControl,
  QuickEventControl,
  findAddContainer
} from "./quick-structure-controls";
import { QuickStyleControl } from "./quick-style-controls";
import { STYLE_LABELS } from "./quick-style-model";

export interface IQuickPropsRegionProps {
  sd: ISelectionOverlaySd;
  kuikaId: string;
  displayItem: IDisplayItem;
  /**
   * The component's RAW definition from the project's component set (not the whitelisted copy
   * `sd.getComponentDefinition` returns) — the curated quick props live on it.
   */
  definition: IComponent | undefined;
  /**
   * The canvas safe area's width. The whole bar stays within it and within `SELECTION_BAR_MAX_WIDTH`;
   * controls that do not fit fold into "+N".
   */
  availableWidth: number | null;
  /** Bumped by the overlay to close every open quick-prop popover. */
  dismissSignal: number;
}

/** One renderable quick prop. */
interface IQuickEntry {
  key: string;
  /** Row label when the entry is folded into the "+N" popover. */
  label: string;
  element: JSX.Element;
}

/** Width reserved for the "+N" chip before it has rendered once (then its measured width is used). */
const MORE_CHIP_WIDTH: number = 44;
/** The bar's `gap` between controls (`--gap-xxs`). */
const GAP_WIDTH: number = 2;

const propertyNamesOf = (quickProps: IQuickProp[]): string[] =>
  quickProps.flatMap((entry: IQuickProp): string[] => {
    if (entry.kind === "property") {
      return [entry.name];
    }
    if (entry.kind === "propertyGroup") {
      return entry.names;
    }
    return [];
  });

const hasEvent = (definition: IComponent, name: string): boolean =>
  definition.properties?.some(
    (p: IPropertyDefinition): boolean => p.name === name && p.type?.includes(EPropertyType.event) === true
  ) === true;

/**
 * The entries of the curated list this designer build can render. Anything else — an unknown `kind` or
 * style key from a newer component set, a malformed entry — is skipped (`warnSkippedQuickProp`), so one
 * bad entry costs one control, not the bar.
 */
function renderableQuickProps(definition: IComponent): IQuickProp[] {
  const raw: unknown = definition.quickProps;
  if (raw === undefined || raw === null) {
    return [];
  }
  if (!Array.isArray(raw)) {
    warnSkippedQuickProp(definition.name, "quickProps is not a list", raw);
    return [];
  }
  return raw.filter((entry: unknown): entry is IQuickProp => {
    const issue: QuickPropShapeIssue | undefined = getQuickPropShapeIssue(entry);
    if (issue !== undefined) {
      warnSkippedQuickProp(definition.name, issue, entry);
      return false;
    }
    return true;
  });
}

/** The curated list → the controls that can actually render for this component right now. */
function buildEntries(ctx: IQuickContext, definition: IComponent): IQuickEntry[] {
  const quickProps: IQuickProp[] = renderableQuickProps(definition);
  const resolved: Map<string, IResolvedQuickProperty> = resolveQuickProperties(ctx, propertyNamesOf(quickProps));
  const entries: IQuickEntry[] = [];
  quickProps.forEach((entry: IQuickProp): void => {
    switch (entry.kind) {
      case "property": {
        const property: IResolvedQuickProperty | undefined = resolved.get(entry.name);
        if (property) {
          entries.push({
            key: `property:${entry.name}`,
            label: property.label,
            element: <QuickPropertyControl ctx={ctx} resolved={property} warnWhenEmpty={entry.warnWhenEmpty} />
          });
        }
        return;
      }
      case "propertyGroup": {
        const group: IQuickPropertyGroupRef = entry;
        const members: IResolvedQuickProperty[] = group.names
          .map((name: string): IResolvedQuickProperty | undefined => resolved.get(name))
          .filter((m: IResolvedQuickProperty | undefined): m is IResolvedQuickProperty => m !== undefined);
        if (members.length > 0) {
          entries.push({
            key: `group:${group.label}`,
            label: toDisplayLabel(group.label),
            element: <QuickPropertyGroup ctx={ctx} entry={group} members={members} />
          });
        }
        return;
      }
      case "event":
        if (hasEvent(definition, entry.name)) {
          entries.push({
            key: `event:${entry.name}`,
            label: toDisplayLabel(entry.name),
            element: <QuickEventControl ctx={ctx} entry={entry} />
          });
        }
        return;
      case "style":
        entries.push({
          key: `style:${entry.key}`,
          label: STYLE_LABELS[entry.key],
          element: <QuickStyleControl ctx={ctx} styleKey={entry.key} />
        });
        return;
      case "command": {
        const canAdd: boolean =
          findAddContainer(ctx.displayItem, entry.childName, (node: IDisplayItem): IComponentDefinition | undefined =>
            ctx.sd.project?.componentSetObj?.components?.find(
              (c: IComponentDefinition): boolean => c.id === node.item?.id
            )
          ) !== undefined;
        if (canAdd) {
          entries.push({
            key: `command:${entry.childName}`,
            label: toDisplayLabel(entry.label ?? entry.childName),
            element: <QuickCommandControl ctx={ctx} entry={entry} />
          });
        }
        return;
      }
      case "children":
        entries.push({
          key: `children:${entry.label}`,
          label: toDisplayLabel(entry.label),
          element: <QuickChildrenControl ctx={ctx} entry={entry} />
        });
        return;
      default: {
        // Unreachable after renderableQuickProps; kept exhaustive so a new kind is a compile error here.
        const unhandled: never = entry;
        warnSkippedQuickProp(definition.name, "unknownKind", unhandled);
        return;
      }
    }
  });
  return entries;
}

interface IMoreChipProps {
  entries: IQuickEntry[];
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  /** The "+N" popover's own open key: its content is the scope the folded controls open theirs in. */
  openKey: string;
}

/**
 * The folded controls, one labelled row each, behind a "+N" chip. The open flag lives in the region:
 * while the popover is open the folded controls render here, otherwise in the region's hidden measurer.
 */
const MoreChip = ({ entries, isOpen, setIsOpen, openKey }: IMoreChipProps): JSX.Element => {
  return (
    <QuickPopover
      open={isOpen}
      onOpenChange={setIsOpen}
      testId="quickPropPopover-more"
      content={
        // The folded controls open their own popovers from in here; the scope keeps "+N" open meanwhile.
        <QuickOpenScope scopeKey={openKey}>
          <div className="kuika-quick-pop__title">More settings</div>
          {entries.map((entry: IQuickEntry): JSX.Element => (
            <div key={entry.key} className="kuika-quick-pop__row">
              <span className="kuika-quick-pop__row-label">{entry.label}</span>
              {entry.element}
            </div>
          ))}
        </QuickOpenScope>
      }
    >
      <QuickTip label="More settings" isSuppressed={isOpen}>
        <QuickChip
          testId="quickPropMore"
          ariaLabel={`${entries.length} more settings`}
          value={`+${entries.length}`}
          hasPopup
          isOpen={isOpen}
          onClick={(): void => setIsOpen(!isOpen)}
        />
      </QuickTip>
    </QuickPopover>
  );
};

const sameKeys = (a: string[] | null, b: string[]): boolean =>
  a !== null && a.length === b.length && a.every((key: string, i: number): boolean => key === b[i]);

/**
 * The left part of the selection bar: the selected component's curated quick props
 * (`IComponentDefinition.quickProps`), each mapped to one generic control by its kind and — for
 * properties — by the tool type the property panel itself would use. Controls that do not fit the
 * canvas fold into a "+N" chip; the bar's generic right section never folds.
 */
export const QuickPropsRegion: React.FC<IQuickPropsRegionProps> = ({
  sd,
  kuikaId,
  displayItem,
  definition,
  availableWidth,
  dismissSignal
}: IQuickPropsRegionProps): JSX.Element | null => {
  const ctx: IQuickContext = { sd, kuikaId, displayItem };
  const entries: IQuickEntry[] = definition ? buildEntries(ctx, definition) : [];
  const entryKeys: string = entries.map((e: IQuickEntry): string => e.key).join("|");

  const rootRef: React.RefObject<HTMLSpanElement> = useRef<HTMLSpanElement>(null);
  const itemRefs: React.MutableRefObject<Map<string, HTMLElement>> = useRef(new Map<string, HTMLElement>());
  const widthsRef: React.MutableRefObject<Map<string, number>> = useRef(new Map<string, number>());
  const moreRef: React.RefObject<HTMLSpanElement> = useRef<HTMLSpanElement>(null);
  const moreWidthRef: React.MutableRefObject<number> = useRef<number>(MORE_CHIP_WIDTH);
  // null = everything shown — also where every new selection / entry set starts, so it gets measured.
  const [visibleKeys, setVisibleKeys] = useState<string[] | null>(null);
  const fitKey: string = `${kuikaId}#${entryKeys}`;
  const [fittedFor, setFittedFor] = useState<string>(fitKey);
  if (fittedFor !== fitKey) {
    setFittedFor(fitKey);
    setVisibleKeys(null);
  }
  const widthsKeyRef: React.MutableRefObject<string> = useRef<string>(fitKey);

  // The "+N" popover's open flag, shared with every other bar popover (opening one closes the rest).
  const [isMoreOpenState, setIsMoreOpen, moreOpenKey]: [boolean, (open: boolean) => void, string] = useQuickOpenKey();

  // Record the width of every control, then fit. Folded controls stay mounted in a hidden measurer, so
  // their widths are real, not what they were when last shown (a value still loading, a font still
  // swapping in) — a stale width could keep a control folded for good. Only while the "+N" popover is
  // open do they live there instead, and the widths from just before stand. The result only changes
  // state when the kept set changes. Runs when the entry set, the room or the kept set changes — and,
  // through the ResizeObserver below, when any control's own width changes — not on every render.
  const fit = (): void => {
    const root: HTMLSpanElement | null = rootRef.current;
    const bar: HTMLElement | null = root?.closest(".kuika-selection-bar") ?? null;
    if (!root || !bar || availableWidth === null || entries.length === 0) {
      return;
    }
    if (widthsKeyRef.current !== fitKey) {
      widthsKeyRef.current = fitKey;
      widthsRef.current.clear();
    }
    itemRefs.current.forEach((element: HTMLElement, key: string): void => {
      widthsRef.current.set(key, element.offsetWidth);
    });
    if (moreRef.current) {
      moreWidthRef.current = Math.max(MORE_CHIP_WIDTH, moreRef.current.offsetWidth);
    }
    // Everything that never folds counts — the bar's padding and border, the gaps, the merged crumb
    // segment, the style tag and the icon buttons — measured as the bar minus this region.
    const budget: IBarBudget = selectionBarBudget({
      barWidth: bar.offsetWidth,
      regionWidth: root.offsetWidth,
      availableWidth
    });
    const widths: number[] = entries.map((e: IQuickEntry): number => widthsRef.current.get(e.key) ?? 0);
    // The fold keeps a set, not a prefix (a narrow later control can stay while a wide earlier one
    // folds), so hysteresis compares against exactly which controls are visible now.
    const currentKept: number[] | null =
      visibleKeys === null
        ? null
        : entries.flatMap((e: IQuickEntry, i: number): number[] => (visibleKeys.includes(e.key) ? [i] : []));
    const kept: number[] = collapsePropsStable(
      {
        itemWidths: widths,
        priorities: entries.map((_e: IQuickEntry, i: number): number => i),
        fixedWidth: budget.fixedWidth,
        moreWidth: moreWidthRef.current,
        gapWidth: GAP_WIDTH,
        maxWidth: budget.maxWidth
      },
      currentKept
    );
    const next: string[] = kept.map((i: number): string => entries[i].key);
    const allShown: boolean = next.length === entries.length;
    if (allShown ? visibleKeys !== null : !sameKeys(visibleKeys, next)) {
      setVisibleKeys(allShown ? null : next);
    }
  };
  const isVisible = (entry: IQuickEntry): boolean => visibleKeys === null || visibleKeys.includes(entry.key);
  const folded: IQuickEntry[] = entries.filter((e: IQuickEntry): boolean => !isVisible(e));
  const isMoreOpen: boolean = folded.length > 0 && isMoreOpenState;
  const hasFolded: boolean = folded.length > 0;
  const setIsMoreOpenRef: React.MutableRefObject<(open: boolean) => void> = useRef(setIsMoreOpen);
  useLayoutEffect((): void => {
    setIsMoreOpenRef.current = setIsMoreOpen;
  });
  // Nothing folded any more: "+N" is gone, and must not come back open. A dismiss closes it too (the
  // provider's reset does that under the overlay; this covers a region rendered without one).
  useEffect((): void => {
    if (!hasFolded) {
      setIsMoreOpenRef.current(false);
    }
  }, [hasFolded]);
  useEffect((): void => {
    setIsMoreOpenRef.current(false);
  }, [dismissSignal]);

  const fitRef: React.MutableRefObject<() => void> = useRef(fit);
  // Declared before the effects that call it, so it runs first in every commit.
  useLayoutEffect((): void => {
    fitRef.current = fit;
  });
  const visibleKeysKey: string = visibleKeys === null ? "*" : visibleKeys.join("|");

  useLayoutEffect((): void => {
    fitRef.current();
  }, [fitKey, availableWidth, visibleKeysKey]);

  useEffect((): (() => void) | undefined => {
    if (typeof ResizeObserver === "undefined") {
      return undefined;
    }
    // Re-fitting changes which items are laid out, which resizes the observed bar in the same frame;
    // running it straight from the callback makes the browser report an undelivered-notifications loop.
    // One re-fit per animation frame, after the observer has delivered, is enough.
    let pendingFrame: number | null = null;
    const observer: ResizeObserver = new ResizeObserver((): void => {
      if (pendingFrame !== null) {
        return;
      }
      pendingFrame = window.requestAnimationFrame((): void => {
        pendingFrame = null;
        fitRef.current();
      });
    });
    itemRefs.current.forEach((element: HTMLElement): void => observer.observe(element));
    // The bar's other controls (the style tag chip's value) change the room left for the quick props.
    const bar: Element | null = rootRef.current?.closest(".kuika-selection-bar") ?? null;
    if (bar) {
      observer.observe(bar);
    }
    if (moreRef.current) {
      observer.observe(moreRef.current);
    }
    return (): void => {
      observer.disconnect();
      if (pendingFrame !== null) {
        window.cancelAnimationFrame(pendingFrame);
      }
    };
  }, [fitKey, visibleKeysKey, isMoreOpen]);

  if (entries.length === 0) {
    return null;
  }

  return (
    <QuickDismissContext.Provider value={dismissSignal}>
      <span ref={rootRef} className="kuika-quick-props" data-testid="quickPropsRegion">
        {entries
          .filter((entry: IQuickEntry): boolean => isVisible(entry) || !isMoreOpen)
          .map((entry: IQuickEntry): JSX.Element => {
            const isMeasureOnly: boolean = !isVisible(entry);
            return (
              <span
                key={entry.key}
                className={
                  isMeasureOnly ? "kuika-quick-props__item kuika-quick-props__item--measure" : "kuika-quick-props__item"
                }
                aria-hidden={isMeasureOnly ? true : undefined}
                ref={(element: HTMLSpanElement | null): void => {
                  if (element) {
                    // React 17 has no `inert` prop; a folded control must not be reachable by Tab.
                    element.toggleAttribute("inert", isMeasureOnly);
                    itemRefs.current.set(entry.key, element);
                  } else {
                    itemRefs.current.delete(entry.key);
                  }
                }}
              >
                {entry.element}
              </span>
            );
          })}
        {folded.length > 0 && (
          <span ref={moreRef} className="kuika-quick-props__more">
            <MoreChip entries={folded} isOpen={isMoreOpen} setIsOpen={setIsMoreOpen} openKey={moreOpenKey} />
          </span>
        )}
      </span>
    </QuickDismissContext.Provider>
  );
};
